"use client";

import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
// Desktop Dialog components
import { Dialog, DialogContent } from "@/components/ui/dialog";
// Mobile Sheet components
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useScreenDetector } from "@/hooks/use-screen-detector";
import { DesktopCreateAppointment } from "./components/desktop-create-appointment";
import { MobileCreateAppointment } from "./components/mobile-create-appointment";

interface CreateAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  prefilledData?: {
    customerId?: string;
    workerId?: string;
    startTime?: Date;
    date?: string;
    time?: string;
    notes?: string;
  };
}

export function CreateAppointmentDialog({
  open,
  onOpenChange,
  onSuccess,
  prefilledData,
}: CreateAppointmentDialogProps) {
  const { t } = useTranslation();
  const { isMobile } = useScreenDetector();

  // Early return if dialog should not be shown
  if (!open) {
    return null;
  }

  // Mobile Sheet
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="h-[90vh] max-h-[90vh] flex flex-col p-0 !rounded-t-2xl bg-clip-border overflow-hidden"
          hideClose
        >
          <div className="flex-shrink-0 border-b shadow-sm px-4 py-1">
            <div className="flex items-center justify-between h-11">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="text-blue-600 font-medium -ml-2 px-2 h-8"
              >
                {t("common:actions.cancel", { defaultValue: "Cancel" })}
              </Button>
              <SheetTitle className="text-base font-semibold">
                {t("appointments:createAppointment", {
                  defaultValue: "Create Appointment",
                })}
              </SheetTitle>
              <div className="w-16" /> {/* Spacer for center alignment */}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto relative">
            <MobileCreateAppointment
              open={open}
              onOpenChange={onOpenChange}
              onSuccess={onSuccess}
              prefilledData={prefilledData}
            />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop Dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[1000px] max-h-[90vh] min-h-[600px] p-0 !rounded-xl bg-clip-border flex flex-col"
        hideClose
      >
        <DesktopCreateAppointment
          open={open}
          onOpenChange={onOpenChange}
          onSuccess={onSuccess}
          prefilledData={prefilledData}
        />
      </DialogContent>
    </Dialog>
  );
}
